'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '~/lib/utils';
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipPortal = TooltipPrimitive.Portal;
const TooltipContent = React.forwardRef<React.ElementRef<typeof TooltipPrimitive.Content>, React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>>(({
  className,
  sideOffset = 4,
  ...props
}, ref) => <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content ref={ref} sideOffset={sideOffset} className={cn(
  // Layout
  // I removed `overflow-hidden` from the original component (https://github.com/shadcn-ui/ui/issues/2753)
  // And added `relative`
  'relative z-50 bg-popover px-3 py-1.5',
  // Borders
  'rounded-md border shadow-md',
  // Text
  'text-left text-sm text-popover-foreground',
  // Animation
  'animate-in data-[state=closed]:animate-out',
  // Zoom
  'zoom-in-95 data-[state=closed]:zoom-out-95',
  // Fade
  'fade-in-0 data-[state=closed]:fade-out-0',
  // Slide
  'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2', className)} {...props} />
  </TooltipPrimitive.Portal>);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
const arrowVariants = cva('absolute h-[13px] w-[24px] ', {
  variants: {
    direction: {
      up: 'bottom-full left-1/2 -translate-x-1/2 rotate-180',
      down: 'left-1/2 top-full -translate-x-1/2 rotate-0',
      left: 'right-full top-1/2 -translate-y-1/2 translate-x-[5.5px] rotate-90',
      right: 'left-full top-1/2 -translate-y-1/2 translate-x-[-5.5px] -rotate-90'
    }
  },
  defaultVariants: {
    direction: 'down'
  }
});

// This is a custom component. You might want to fix the styles if needed.
// Use sideOffset={8} on `TooltipComponent` when using it with this arrow.
const TooltipArrow = ({
  direction = 'down'
}: VariantProps<typeof arrowVariants>) => {
  return <div className={arrowVariants({
    direction
  })} data-sentry-component="TooltipArrow" data-sentry-source-file="tooltip.tsx">
      <svg width="24" height="13" viewBox="0 0 24 13" className="fill-white stroke-border" data-sentry-element="svg" data-sentry-source-file="tooltip.tsx">
        <path d="M24 0.5L18.0099 0.5C17.3892 0.5 16.8327 0.882244 16.6099 1.46153L13.0363 10.7528C12.5432 12.0348 10.7294 12.0348 10.2363 10.7528L6.66272 1.46153C6.43992 0.882242 5.88336 0.499999 5.2627 0.499999L3.97331e-07 0.499999L4.13225e-07 -9.53674e-07L4.29118e-07 -0.500001L5.2627 -0.500001L18.0099 -0.5L24 -0.5L24 -1.90798e-07L24 0.5Z" data-sentry-element="path" data-sentry-source-file="tooltip.tsx" />
      </svg>
    </div>;
};
export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipPortal, TooltipArrow };